import * as React from 'react';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { IconName as FaIconName } from '@fortawesome/fontawesome-common-types';
import { blacklist, Theme, defaultTheme, iconPrefix, IconName } from './utility';
import { Transform, useTransform } from './utility/useTransform';
import { useIconMap } from './utility/useIconMap';
import { Mask, useMask } from './utility/useMask';
import { DuoToneStyle, useDuoTone } from './utility/useDuoTone';
import { useAnimation } from './utility/useAnimation';
import { useBasicStyling } from './utility/useBasicStyling';

export type SizeProp = '2xs' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl' | '1x' | '2x' | '3x' | '4x' | '5x' | '6x' | '7x' | '8x' | '9x' | '10x';

export type IconProps = Omit<FontAwesomeIconProps, 'forwardedRef' | 'pull' | 'rotate' | 'icon' | 'spin' | 'mask' | 'flip' | 'size' | 'transform'> &
	React.ComponentPropsWithRef<'i'> & {
		// overridden properties
		icon: IconName;
		spin?: boolean | 'reverse';
		mask?: Mask;
		flip?: boolean | 'horizontal' | 'vertical' | 'both';
		size?: SizeProp;
		transform?: string | Transform;
		// custom properties
		theme?: Theme;
		beat?: boolean;
		fade?: boolean;
		bounce?: boolean;
		shake?: boolean;
		stack?: '1x' | '2x';
		primaryStyle?: DuoToneStyle;
		secondaryStyle?: DuoToneStyle;
		'data-testid'?: string;
	};

const Icon = React.forwardRef((props: IconProps, ref: React.ForwardedRef<HTMLElement>): JSX.Element | null => {
	const {
		icon: initialIcon,
		theme: initialTheme,
		style,
		title,
		className,
		color,
		fixedWidth,
		size,
		inverse,
		border,
		rotation,
		stack,
		mask,
		bounce,
		flip,
		shake,
		spin,
		pulse,
		beat,
		fade,
		transform,
		swapOpacity,
		primaryStyle,
		secondaryStyle,
		...iconProps
	} = props;
	const { icon, theme = defaultTheme } = useIconMap(initialIcon, initialTheme);
	if (blacklist.includes(icon as FaIconName)) {
		return null;
	}

	const transformProps = useTransform(transform);
	const maskProps = useMask({ mask, theme: initialTheme });
	const { classList: basicStylingClassList } = useBasicStyling({ size, inverse, fixedWidth, border, rotation, stack });
	const { classList: animationClassList } = useAnimation({ bounce, flip, shake, spin, pulse, beat, fade });
	const { classList: duoToneClassList, style: duoToneStyle } = useDuoTone({ theme: initialTheme, swapOpacity, primaryStyle, secondaryStyle });

	const classes = [className, `${iconPrefix}-${theme}`, `${iconPrefix}-${icon}`, ...basicStylingClassList, ...animationClassList, ...duoToneClassList]
		.filter(Boolean)
		.join(' ');

	const iconStyle = {
		color,
		...duoToneStyle,
		...style
	};

	return <i aria-hidden="true" title={title} ref={ref} className={classes} style={iconStyle} {...iconProps} {...maskProps} {...transformProps} />;
});
Icon.displayName = 'Icon';

export default Icon;
